<template>
  <div class="pt-2">
    <div class="phone-numbers bg-white dark:bg-gray-800 relative shadow-sm sm:rounded-lg border border-gray-200">
      <div class="flex gap-4 p-4">

        <!-- Search Input -->
        <div class="md:w-1/3">
          <label for="phone-numbers-search" class="sr-only">Search</label>
          <div class="relative w-full">
            <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg aria-hidden="true" class="w-5 h-5 text-gray-500 dark:text-gray-400" fill="currentColor"
                viewbox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clip-rule="evenodd" />
              </svg>
            </div>
            <input type="text" id="phone-numbers-search"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full h-10 pl-10 p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
              placeholder="Search" required=""
              v-model="search">
          </div>
        </div>

        <div class="w-96">
          <multi-select
            v-model="selectedStatuses"
            :options="statusesList"
            placeholder="All statuses"
            class="multiselect--tags-blue text-sm bg-gray-50"
            input-container-class="bg-gray-50 rounded-lg"
            label="label"
            track-by="name"
            :multiple="true"
            :searchable="false"
            :close-on-select="false"
            :clear-on-select="false">

            <template #selection="{ values, isOpen }">
              <span
                v-if="values.length"
                v-show="!isOpen">
                <span v-if="values.length === 1" class="multiselect__tag pr-2">
                  {{ values[0].label }}
                </span>
                <span v-if="values.length > 1"
                  class="flex">
                  <span class="multiselect__tag pr-2">
                    {{ values[0].label }}
                  </span>
                  and {{ values.length - 1 }} statuses selected
                </span>
              </span>
            </template>
          </multi-select>
        </div>

        <!-- New Phone Button -->
        <div
          class="ml-auto">
          <a
            href="/phone_numbers/new"
            class="px-5 py-2 h-10 text-sm font-medium text-white inline-flex items-center bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">

            <svg class="w-5 h-5 me-2 text-gray-200 dark:text-white" aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M5 12h14m-7 7V5" />
            </svg>

            Phone Number
          </a>
        </div>
      </div>

      <!-- Table, Pagination and Footer -->
      <!-- eslint-disable vue/no-unused-vars, vue/no-template-shadow -->
      <vue-slim-tables
        ref="table"
        :source="tableSource"
        :columns="columns"
        class="table"
        :per-page="pageSize">
        <template #thead="{ columns, orders }">
          <tr>
            <th :class="columns[0].class">
              {{ columns[0].title }}
            </th>

            <th :class="columns[1].class">
              {{ columns[1].title }}
            </th>

            <th :class="columns[2].class">
              {{ columns[2].title }}
            </th>

            <th :class="columns[3].class">
              {{ columns[3].title }}
            </th>

            <th :class="columns[4].class">
              {{ columns[4].title }}
            </th>
          </tr>
        </template>

        <template #cell:name="{ row, index, column, value }">
          <span class="truncate w-48">
            {{ value }}
          </span>
        </template>

        <template #cell:provider="{ row, index, column, value }">
          <div>
            {{ capitalize(value) }}
          </div>
        </template>

        <template #cell:status="{ row, index, column, value }">
          <div class=" whitespace-nowrap">
            <span>
              <span v-if="numberStatusOk(row)"
                class="inline-block text-center bg-green-100 text-green-800 text-xs font-medium pl-1 pr-1.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                <svg class="w-4 h-4 text-green-400 inline" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                  width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M5 11.917 9.724 16.5 19 7.5" />
                </svg>

                {{ humanize(row.status) }}
              </span>
              <span v-else
                class="inline-block text-center bg-red-100 text-red-800 text-xs font-medium pl-1 pr-1.5 py-0.5 rounded dark:bg-red-900 dark:text-red-300">
                <svg class="w-4 h-4 text-red-400 inline" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                  fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7.757 12h8.486M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>

                {{ humanize(row.status) }}
              </span>
            </span>
          </div>
        </template>

        <template #cell:actions="{ row, index, column, value }">
          <div>
            <context-menu>
              <ul class="py-1 text-sm text-gray-700 dark:text-gray-200">
                <li>
                  <a
                    :href="`/phone_numbers/${row.id}/edit`"
                    class="block py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white">
                    Configure
                  </a>
                </li>
              </ul>
              <div class="py-1">
                <a @click="deletePhoneNumber(row.id, row.name, row.number)"
                  class="block py-2 px-4 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 dark:hover:text-white">
                  Delete
                </a>
              </div>
            </context-menu>
          </div>
        </template>

        <template #pagination>
          <nav class="flex flex-col md:flex-row justify-between items-start md:items-center space-y-3 md:space-y-0">
            <span class="text-sm font-normal text-gray-500 dark:text-gray-400">
              Showing
              <span class="font-semibold text-gray-900 dark:text-white">{{ currentPageInfo() }}</span>
              of
              <span class="font-semibold text-gray-900 dark:text-white">{{ totalCount }}</span>
            </span>

            <VuejsPaginate
              v-if="totalPagesCount > 1"
              v-model="currentPage"
              :page-count="totalPagesCount"
              :prev-text="'<'"
              :next-text="'>'" />
          </nav>
        </template>
      </vue-slim-tables>
      <!-- eslint-enable vue/no-unused-vars, vue/no-template-shadow -->
    </div>
  </div>
</template>

<script setup>
import { watch, ref } from 'vue'
import VueSlimTables from 'vue-slim-tables'
import 'vue-slim-tables/stylesheets'
import VuejsPaginate from 'vuejs-paginate-next'
import ContextMenu from '../common/context_menu.vue'
import securedAxios from '../../utils/axios'
import qs from 'qs'
import { toastError, toastSuccess } from '~/javascript/utils/toast'
import { confirmAlert } from '../../utils/confirm_alert'
import { capitalize, humanize } from '~/javascript/utils/strings'
import MultiSelect from 'vue-multiselect'

const axios = securedAxios()

const columns = [
  { title: 'Friendly Name', key: 'name', class: 'w-48' },
  { title: 'Number', key: 'number', class: 'w-48' },
  { title: 'Provider', key: 'provider', class: 'w-auto' },
  { title: 'Status', key: 'status', class: 'w-2' },
  { title: '', key: 'actions', class: 'w-2' }
]

const table = ref()
const search = ref('')
const totalPagesCount = ref(200)
const totalCount = ref(0)
const currentPage = ref(1)
const pageSize = ref(10)

const statusesList = ref([
  { name: 'active', label: 'Active' },
  { name: 'inactive', label: 'Inactive' },
  { name: 'not_configured', label: 'Not configured' },
  { name: 'unavailable', label: 'Unavailable' }
])
const selectedStatuses = ref([])

let searchTriggerTimeout = null

function numberStatusOk(number) {
  return number.status === 'active'
}

async function tableSource(params) {
  let response = null
  const statuses = selectedStatuses.value.map((status) => status.name)

  try {
    response = await axios.get(
      '/ajax/phone_numbers',
      {
        params: { ...params, query: search.value, page: currentPage.value, statuses: statuses },
        paramsSerializer(json) {
          return qs.stringify(json, { arrayFormat: 'brackets' })
        }
      }
    )
  } catch (error) {
    toastError(error)
  }

  totalPagesCount.value = parseInt(response.headers['total-pages'], 10)
  totalCount.value = parseInt(response.headers['total-count'], 10)
  currentPage.value = parseInt(response.headers['page-number'], 10)
  pageSize.value = parseInt(response.headers['page-size'], 10)

  return response?.data || []
}

function currentPageInfo() {
  let firstVisible = pageSize.value * (currentPage.value - 1) + 1
  const lastVisible = Math.min(firstVisible + pageSize.value - 1, totalCount.value)

  if (firstVisible > lastVisible) {
    firstVisible = lastVisible
  }

  return `${firstVisible}-${lastVisible}`
}

async function deletePhoneNumber(id, name, number) {
  const ok = await confirmAlert({
    html: `<p>Are you sure you want to delete phone number</p>
            <p class="mt-1"></p><strong>${name}</strong> <small>/${number}</small> ?</p>
            <p class="mt-2">Once deleted, you will not be able to recover this!</p>`
  })

  if (!ok) {
    return
  }

  try {
    // It is important to show progress bar for long-running requests.
    Turbo.navigator.delegate.adapter.formSubmissionStarted()
    await axios.delete(`/ajax/phone_numbers/${id}`)

    toastSuccess('Phone number deleted successfully!')
    table?.value?.reload()
  } catch (error) {
    toastError(error)
  } finally {
    Turbo.navigator.delegate.adapter.formSubmissionFinished()
  }
}

watch(currentPage, (_val) => {
  table?.value?.reload()
})

watch(selectedStatuses, (_val) => {
  table?.value?.reload()
})

watch(search, (_val) => {
  clearTimeout(searchTriggerTimeout)
  searchTriggerTimeout = setTimeout(() => {
    // Reset the page number to 1 when searching otherwise the table will crash
    currentPage.value = 1
    table?.value?.reload()
  }, 500)
})

</script>
